import * as React from "react";
import { Slot, Slottable } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";

import { FOCUS_RING } from "../lib/focus";
import { cn } from "../lib/utils";
import { Icon, type IconComponent } from "./icon";
import { Throbber } from "./throbber";
import { Tooltip, TooltipContent, TooltipTrigger } from "./tooltip";

/**
 * v2 Button (redesign phase 5, #455). Storybook only until implementation;
 * see docs/design-library/04-implementation-notes.md for the swap.
 *
 * States are styled twice: by the pseudo-class (`hover:`, `active:`,
 * `focus-visible:`) and by a bare data attribute (`data-hover`, `data-active`,
 * `data-focus`, Headless UI's state API). The attributes let stories show a
 * state statically; apps never set them.
 *
 * Disabled applies for both the native attribute and `aria-disabled` (e.g. a
 * link under `asChild`). Loading is NOT disabled: the button keeps its colour
 * and focus, sets `aria-busy` and ignores clicks.
 *
 * `disabled` + `disabledReason` (finding #21, the NavItem approach): the
 * button stays focusable (`aria-disabled`, not the native attribute), the
 * reason is its description and a tooltip on hover and focus, and clicks are
 * dropped in the handler, so the pointer still reaches it for the tooltip.
 */

export const BUTTON_BASE =
  "relative inline-flex shrink-0 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md font-sans font-semibold transition-colors " +
  `${FOCUS_RING} ` +
  "disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 aria-busy:pointer-events-none";

export const BUTTON_VARIANTS = {
  /** The main action: one per surface. */
  default:
    "bg-primary text-primary-foreground hover:bg-primary-hover data-hover:bg-primary-hover active:bg-primary-active data-active:bg-primary-active",
  /** Secondary actions. Transparent fill: inherits page, card or popover. */
  outline:
    "border border-border bg-transparent text-ink hover:bg-hover-overlay data-hover:bg-hover-overlay active:bg-active-overlay data-active:bg-active-overlay",
  ghost:
    "bg-transparent text-ink hover:bg-hover-overlay data-hover:bg-hover-overlay active:bg-active-overlay data-active:bg-active-overlay",
  /** Filled green: the positive action, such as confirming (#820). One per surface, like `default`. */
  success:
    "bg-success text-success-foreground hover:bg-success-hover data-hover:bg-success-hover active:bg-success-active data-active:bg-success-active",
  /** Filled: the confirm button of a destructive dialog. */
  destructive:
    "bg-destructive text-destructive-foreground hover:bg-destructive-hover data-hover:bg-destructive-hover active:bg-destructive-active data-active:bg-destructive-active",
  /** Quiet: destructive items in menus and rows (break #28). */
  "destructive-ghost":
    "bg-transparent text-destructive-accent hover:bg-destructive-subtle data-hover:bg-destructive-subtle active:bg-destructive-subtle active:text-destructive-strong data-active:bg-destructive-subtle data-active:text-destructive-strong",
  /** Inline text action: no height, no padding (break #30). */
  link: "h-auto px-0 text-primary-accent underline-offset-4 hover:underline data-hover:underline active:underline data-active:underline",
} as const;

export type ButtonVariant = keyof typeof BUTTON_VARIANTS;
export type ButtonSize = "sm" | "md" | "lg";

const buttonVariants = cva(BUTTON_BASE, {
  variants: {
    // Declared before `variant` so `link`'s `h-auto px-0` wins the merge.
    size: {
      sm: "h-7 px-2 text-caption font-semibold",
      md: "h-8 px-3 text-label",
      lg: "h-9 px-4 text-label",
    } satisfies Record<ButtonSize, string>,
    variant: BUTTON_VARIANTS,
  },
  defaultVariants: { variant: "default", size: "md" },
});

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Rendered at 16px / stroke 1.75. While loading, the spinner takes its place. */
  leadingIcon?: IconComponent;
  trailingIcon?: IconComponent;
  /** `aria-busy`, clicks ignored, width fixed. */
  loading?: boolean;
  fullWidth?: boolean;
  /** Render the single child element (e.g. `<a>`) as the button; icons and loading still apply. */
  asChild?: boolean;
  /** With `disabled`: why. The button stays focusable and says it (description + tooltip). German UI text. */
  disabledReason?: string;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "default",
    size = "md",
    leadingIcon,
    trailingIcon,
    loading = false,
    fullWidth = false,
    asChild = false,
    className,
    children,
    onClick,
    type,
    disabled,
    disabledReason,
    ...props
  },
  ref,
) {
  const reasonId = React.useId();
  // Without a leading icon the spinner is centred over the content, which goes
  // transparent so the width stays. `opacity-0`, not `invisible`:
  // visibility:hidden would drop the label from the accessible name.
  const overlay = loading && !leadingIcon;

  // Decorative: aria-busy says it, and a status role would join the button's name.
  const lead = leadingIcon ? loading ? <Throbber decorative /> : <Icon icon={leadingIcon} /> : null;
  const trail = trailingIcon ? <Icon icon={trailingIcon} className={overlay ? "opacity-0" : undefined} /> : null;
  const spinner = overlay ? (
    <span className="absolute inset-0 flex items-center justify-center">
      <Throbber decorative />
    </span>
  ) : null;
  const label = (content: React.ReactNode) => (
    <span className={cn("min-w-0 truncate", overlay && "opacity-0")}>{content}</span>
  );

  const shared = {
    className: cn(buttonVariants({ variant, size }), fullWidth && "w-full", className),
    "aria-busy": loading || undefined,
    // Enter/Space still reach a focused button; pointer-events-none only stops the mouse.
    onClick: loading ? (event: React.MouseEvent<HTMLButtonElement>) => event.preventDefault() : onClick,
  };

  if (disabled && disabledReason && !asChild) {
    return (
      <>
        <Tooltip>
          {/* Not Radix's description: the reason has its own id and is read on focus without the tooltip. */}
          <TooltipTrigger asChild aria-describedby={reasonId}>
            <button
              ref={ref}
              type={type ?? "button"}
              {...shared}
              {...props}
              aria-disabled="true"
              className={cn(shared.className, "cursor-not-allowed aria-disabled:pointer-events-auto")}
              onClick={(event) => event.preventDefault()}
            >
              {lead}
              {label(children)}
              {trail}
              {spinner}
            </button>
          </TooltipTrigger>
          <TooltipContent>{disabledReason}</TooltipContent>
        </Tooltip>
        <span id={reasonId} className="sr-only">
          {disabledReason}
        </span>
      </>
    );
  }

  if (asChild && React.isValidElement<{ children?: React.ReactNode }>(children)) {
    return (
      // `disabled` reaches the child as before; spread, since Slot's props don't declare it.
      <Slot ref={ref} {...shared} {...props} {...{ disabled }}>
        {lead}
        <Slottable>{React.cloneElement(children, undefined, label(children.props.children))}</Slottable>
        {trail}
        {spinner}
      </Slot>
    );
  }

  return (
    // type="button" by default so a button inside a form doesn't submit it.
    <button ref={ref} type={type ?? "button"} disabled={disabled} {...shared} {...props}>
      {lead}
      {label(children)}
      {trail}
      {spinner}
    </button>
  );
});

export { Button, buttonVariants };
